/**
 * PDF Download Button Component
 * 
 * Reusable button component for downloading PDFs with loading states and error handling.
 * Replaces the old print button functionality.
 */

import * as React from 'react';
import { useState, ReactElement } from 'react';
import { IconButton, Button, CircularProgress, Tooltip } from '@mui/material';
import { PictureAsPdf as PdfIcon, Download as DownloadIcon } from '@mui/icons-material';
import { downloadPDF, PDFGenerationStatus, getPDFStatusMessage } from '../utils/pdfDownload';

interface PDFDownloadButtonProps {
  /** The PDF document to generate (React element with @react-pdf/renderer components) */
  document: ReactElement;
  /** Filename for the downloaded PDF (without .pdf extension) */
  filename: string;
  /** Button variant - 'icon' for IconButton, 'contained' or 'outlined' for Button */
  variant?: 'icon' | 'contained' | 'outlined';
  /** Button size */
  size?: 'small' | 'medium' | 'large';
  /** Custom button text (only for non-icon variants) */
  children?: React.ReactNode;
  /** Locale for status messages */
  locale?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Custom onClick handler (called after successful download) */
  onSuccess?: () => void;
  /** Custom error handler */
  onError?: (error: Error) => void;
  /** Custom loading handler */
  onLoadingChange?: (loading: boolean) => void;
}

export const PDFDownloadButton: React.FC<PDFDownloadButtonProps> = ({
  document: pdfDocument,
  filename,
  variant = 'icon',
  size = 'medium',
  children,
  locale = 'fr',
  disabled = false,
  onSuccess,
  onError,
  onLoadingChange
}) => {
  const [status, setStatus] = useState<PDFGenerationStatus>(PDFGenerationStatus.IDLE);
  const [error, setError] = useState<string | null>(null);

  const isLoading = status === PDFGenerationStatus.GENERATING;
  const isDisabled = disabled || isLoading;

  const handleDownload = async () => {
    try {
      setStatus(PDFGenerationStatus.GENERATING);
      setError(null);
      onLoadingChange?.(true);

      await downloadPDF(pdfDocument, filename);

      setStatus(PDFGenerationStatus.SUCCESS);
      onSuccess?.();
      
      // Reset to idle after a short delay
      setTimeout(() => setStatus(PDFGenerationStatus.IDLE), 2000);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error.message);
      setStatus(PDFGenerationStatus.ERROR);
      onError?.(error);
      
      // Reset to idle after showing error
      setTimeout(() => {
        setStatus(PDFGenerationStatus.IDLE);
        setError(null);
      }, 3000);
    } finally {
      onLoadingChange?.(false);
    }
  };

  const getTooltipTitle = () => {
    if (error) return error;
    return getPDFStatusMessage(status, locale);
  };

  const getButtonContent = () => {
    if (isLoading) {
      return <CircularProgress size={variant === 'icon' ? 20 : 16} color="inherit" />;
    }
    
    if (variant === 'icon') {
      return <PdfIcon />;
    }
    
    // Get localized fallback text based on locale
    const fallbackText = locale === 'es' ? 'Descargar PDF' : 
                        locale === 'fr' ? 'Télécharger PDF' : 
                        'Download PDF';
    
    return (
      <>
        <DownloadIcon sx={{ mr: 1 }} />
        {children || fallbackText}
      </>
    );
  };

  if (variant === 'icon') {
    return (
      <Tooltip title={getTooltipTitle()}>
        <span>
          <IconButton
            onClick={handleDownload}
            disabled={isDisabled}
            size={size}
            color={status === PDFGenerationStatus.SUCCESS ? 'success' : 'primary'}
            sx={{
              backgroundColor: status === PDFGenerationStatus.SUCCESS ? 'success.main' : 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: status === PDFGenerationStatus.SUCCESS ? 'success.dark' : 'primary.dark',
              },
              '&.Mui-disabled': {
                backgroundColor: 'action.disabled',
                color: 'action.disabled',
              },
              borderRadius: 2,
              padding: 1.5
            }}
          >
            {getButtonContent()}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  // Get localized fallback text based on locale
  const fallbackText = locale === 'es' ? 'Descargar PDF' : 
                      locale === 'fr' ? 'Télécharger PDF' : 
                      'Download PDF';

  return (
    <Tooltip title={getTooltipTitle()}>
      <span>
        <Button
          variant={variant}
          size={size}
          onClick={handleDownload}
          disabled={isDisabled}
          color={status === PDFGenerationStatus.SUCCESS ? 'success' : 'primary'}
          startIcon={isLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
        >
          {children || fallbackText}
        </Button>
      </span>
    </Tooltip>
  );
};

export default PDFDownloadButton;
